import { HttpRequest } from "../utils"

const httpRequest = new HttpRequest()

export interface IInboundOrderItem {
  id: string
  inboundOrderId: string
  productId: string | null
  uncataloguedItemName: string | null
  uncataloguedSku: string | null
  unit: string | null
  requestedQuantity: number
  dispatchedQuantity: number | null
  receivedQuantity: number | null
  unitCost: number | null
  condition: string | null
  notes: string | null
  photoUrls: string[] | null
}

export interface IInboundOrder {
  id: string
  inboundNumber: string
  sourceType: string
  fromWarehouseId: string | null
  externalPartyId: string | null
  externalPartyAddressOverride: string | null
  externalPartyContactName: string | null
  externalPartyContactPhone: string | null
  toWarehouseId: string
  toWarehouseName: string | null
  /** Item lines on the order; set by the list endpoint only. */
  itemCount?: number
  fromWarehouseName: string | null
  linkedFulfillmentId: string | null
  linkedOrderId: string | null
  status: string
  driverId: string | null
  vehicleId: string | null
  /** BE field names (contract §2 O130): cancelledReason, notes, cancelledAt, pickupCompletedAt. */
  cancelledReason: string | null
  cancelledAt: string | null
  cancelledByUserId: string | null
  notes: string | null
  pickupCompletedAt: string | null
  /** Set by pickup-start; with status DISPATCHED_PICKUP it means "pickup started" (O124/O134). */
  custodyTransferOutboundId: string | null
  /** Only present on GET /inbound-order/:id; the list does not include items (O127). */
  items?: IInboundOrderItem[]
  createdAt: string
  updatedAt: string
}

export interface IInboundOrdersListResponse {
  rows: IInboundOrder[]
  count: number
}

export interface IInboundException {
  id: string
  inboundOrderId: string
  inboundOrderItemId: string | null
  exceptionType: string
  expectedQuantity: number | null
  actualQuantity: number | null
  conditionObserved: string | null
  description: string
  photoUrls: string[] | null
  status: string
  resolutionNotes: string | null
  createdAt: string
  updatedAt: string
}

export interface ICreateInboundInput {
  sourceType: string
  fromWarehouseId?: string | null
  externalPartyId?: string | null
  externalPartyAddressOverride?: string | null
  externalPartyContactName?: string | null
  externalPartyContactPhone?: string | null
  toWarehouseId: string
  linkedFulfillmentId?: string | null
  linkedOrderId?: string | null
  items: Array<{
    productId?: string | null
    uncataloguedItemName?: string | null
    uncataloguedSku?: string | null
    unit?: string | null
    requestedQuantity: number
    unitCost?: number | null
    notes?: string | null
  }>
}

export const create = async (data: ICreateInboundInput): Promise<IInboundOrder> => {
  return httpRequest.post("/ord/inbound-order", data as any)
}

export const list = async (data: {
  filter?: Record<string, unknown>
  offset?: { limit: number; skip: number }
}): Promise<IInboundOrdersListResponse> => {
  return httpRequest.post("/ord/inbound-order/list", data as any)
}

export const get = async (id: string): Promise<IInboundOrder> => {
  return httpRequest.get(`/ord/inbound-order/${id}`)
}

export const update = async (id: string, patch: Partial<ICreateInboundInput>): Promise<IInboundOrder> => {
  return httpRequest.put(`/ord/inbound-order/${id}`, patch as any)
}

const transition =
  (action: string) =>
  async (id: string, body: any = {}): Promise<IInboundOrder> =>
    httpRequest.post(`/ord/inbound-order/${id}/${action}`, body)

export const approve     = transition("approve")
export const dispatch    = transition("dispatch")     // body: {driverId, vehicleId?}
export const cancel      = transition("cancel")       // body: {reason}
export const pickupStart    = transition("pickup-start")    // body: {pin, fromUserId?, toUserId?}
export const pickupComplete = transition("pickup-complete") // body: {pin, receivedItems}
export const arrive      = transition("arrive")       // body: {capturedLatitude?, capturedLongitude?}

// RC-A — receive returns the ReceiveResult (not just the order). `stockPosted`
// is false when the post-receipt нөöц increment failed; the UI must surface it
// and offer the repost retry.
export interface IReceiveResult {
  order: IInboundOrder
  custodyTransferInboundId: string
  exceptions: IInboundException[]
  stockPosted: boolean
  stockError?: string
}

export const receive = async (
  id: string,
  body: {
    driverPin: string
    receiverNotes?: string
    items: Array<{ inboundOrderItemId: string; receivedQuantity: number; condition: string; photoUrls?: string[]; notes?: string }>
  },
): Promise<IReceiveResult> =>
  httpRequest.post(`/ord/inbound-order/${id}/receive`, body as any)

// RC-A — idempotent retry of the warehouse stock increment for an already-
// RECEIVED order whose original post failed.
export interface IRepostStockResult {
  inboundOrderId: string
  reposted: boolean
  stockPosted: boolean
  alreadyPosted: boolean
  resolvedExceptionIds: string[]
  stockError?: string
}

export const repostStock = async (id: string): Promise<IRepostStockResult> =>
  httpRequest.post(`/ord/inbound-order/${id}/repost-stock`, {})

// Exceptions
export const listExceptions = async (id: string): Promise<IInboundException[]> => {
  return httpRequest.get(`/ord/inbound-order/${id}/exceptions`)
}

const exceptionTransition =
  (action: string) =>
  async (id: string, exId: string, body: any = {}): Promise<IInboundException> =>
    httpRequest.post(`/ord/inbound-order/${id}/exceptions/${exId}/${action}`, body)

export const raiseException   = async (id: string, body: any): Promise<IInboundException> =>
  httpRequest.post(`/ord/inbound-order/${id}/exceptions/raise`, body)

export const startException   = (id: string, exId: string) =>
  exceptionTransition("start")(id, exId)

export const resolveException = (id: string, exId: string, body: { resolutionNotes: string }) =>
  exceptionTransition("resolve")(id, exId, body)

export const closeException   = (id: string, exId: string, body: { notes?: string }) =>
  exceptionTransition("close")(id, exId, body)
